import "./App.css";
import {
  request,
  setAccessToken,
  clearAccessToken,
  restoreSession,
  logout,
  setUserLoadedCallback,
} from "./helper/axios_helper";
import { useState, useEffect } from "react";
import HomePage from "./pages/HomePage";
import SignIn from "./pages/Signin";
import { message, Spin } from "antd";
import { useUser } from "./context/UserContext";
import { ProjectProvider } from "./context/ProjectContext";

function App() {
  const [componentToShow, setComponentToShow] = useState("loading");
  const [loginInvalid, setLoginInvalid] = useState(false);
  const [loading, setLoading] = useState(false);

  const { setLoggedInUser, clearUser } = useUser();

  // ─── On mount: wire callback + restore session ────────────
  useEffect(() => {
    // Tell axios_helper to update UserContext whenever a silent
    // token refresh returns new user data
    setUserLoadedCallback(setLoggedInUser);

    restoreSession().then((data) => {
      if (data) {
        // notifyUserLoaded already called inside restoreSession
        // so UserContext is populated — just show the home page
        setComponentToShow("home");
      } else {
        setComponentToShow("login");
      }
    });
  }, [setLoggedInUser]);

  // ─── Handle force logout (session expired) ────────────────
  useEffect(() => {
    const handleForceLogout = () => {
      clearUser();
      window.history.replaceState(null, "", "/");
      setComponentToShow("login");
      message.warning("Your session has expired. Please log in again.");
    };
    window.addEventListener("auth:logout", handleForceLogout);
    return () => window.removeEventListener("auth:logout", handleForceLogout);
  }, [clearUser]);

  // ─── Login ────────────────────────────────────────────────
  const onLogin = (e, username, password) => {
    setLoading(true);
    request("POST", "/api/users/login", { email: username, password: password })
      .then((response) => {
        setLoginInvalid(false);
        setAccessToken(response.data.token);

        // Populate UserContext — now available everywhere via useUser()
        setLoggedInUser({
          email: response.data.email,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
        });

        setComponentToShow("home");
        setLoading(false);
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          message.error("Incorrect username or password!");
          setLoginInvalid(true);
        } else {
          message.error("Something went wrong. Please try again.");
        }
        clearAccessToken();
        setComponentToShow("login");
        setLoading(false);
      });
  };

  // ─── Logout ───────────────────────────────────────────────
  const onLogout = async () => {
    await logout();
    clearUser();
    window.history.replaceState(null, "", "/");
    setComponentToShow("login");
  };

  // ─── Loading state while checking session ─────────────────
  if (componentToShow === "loading") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="App">
      {componentToShow === "login" && (
        <SignIn
          onLogin={onLogin}
          loginInvalid={loginInvalid}
          loading={loading}
        />
      )}
      {componentToShow === "home" && (
        <div>
          {/* ProjectProvider must sit inside the authenticated tree — it calls
              /api/auth/* on mount, which would 401 above the login screen. */}
          <ProjectProvider>
            <HomePage onLogout={onLogout} />
          </ProjectProvider>
        </div>
      )}
    </div>
  );
}

export default App;
